import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Settings } from 'lucide-react';
import { companyApi, brandsApi, sectorsApi } from '../../services/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [brands, setBrands] = useState([]);
  const [sectors, setSectors] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, brandsRes, sectorsRes] = await Promise.all([
          companyApi.get(),
          brandsApi.getAll(),
          sectorsApi.getAll(),
        ]);
        setCompany(companyRes.data);
        setBrands(brandsRes.data);
        setSectors(sectorsRes.data);
      } catch (error) {
        console.error('Error fetching header data:', error);
      }
    };
    fetchData();
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            {company?.logo && (
              <img
                src={company.logo}
                alt={company.name}
                className="h-12 w-auto"
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-gray-900 ${
                isActive('/') ? 'text-gray-900' : 'text-gray-600'
              }`}
            >
              Home
            </Link>

            {/* Brands Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsBrandsOpen(true)}
              onMouseLeave={() => setIsBrandsOpen(false)}
            >
              <button
                className={`flex items-center text-sm font-medium transition-colors hover:text-gray-900 ${
                  location.pathname.includes('/brand') ? 'text-gray-900' : 'text-gray-600'
                }`}
              >
                Brands
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isBrandsOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              <div
                className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 transition-all duration-200 ${
                  isBrandsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                {brands.map((brand) => (
                  <Link
                    key={brand.id}
                    to={`/brand/${brand.id}`}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <span className="font-medium">{brand.name}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{brand.country}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Sectors Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsSectorsOpen(true)}
              onMouseLeave={() => setIsSectorsOpen(false)}
            >
              <button
                className={`flex items-center text-sm font-medium transition-colors hover:text-gray-900 ${
                  location.pathname.includes('/sector') ? 'text-gray-900' : 'text-gray-600'
                }`}
              >
                Sectors
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isSectorsOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Sectors Dropdown Menu */}
              <div
                className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 transition-all duration-200 ${
                  isSectorsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                {sectors.map((sector) => (
                  <Link
                    key={sector.id}
                    to={`/sector/${sector.id}`}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    <span className="font-medium">{sector.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/news"
              className={`text-sm font-medium transition-colors hover:text-gray-900 ${
                isActive('/news') ? 'text-gray-900' : 'text-gray-600'
              }`}
            >
              News
            </Link>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-gray-900 ${
                isActive('/about') ? 'text-gray-900' : 'text-gray-600'
              }`}
            >
              About
            </Link>

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-gray-900 ${
                isActive('/contact') ? 'text-gray-900' : 'text-gray-600'
              }`}
            >
              Contact
            </Link>

            {/* Admin Link */}
            <Link
              to="/admin"
              className="text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
              title="Admin Panel"
            >
              <Settings className="h-4 w-4" />
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-100 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
          <Link
            to="/"
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>

          <div className="px-4 py-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Brands</span>
          </div>
          {brands.map((brand) => (
            <Link
              key={brand.id}
              to={`/brand/${brand.id}`}
              className="block px-8 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              {brand.name}
            </Link>
          ))}

          <div className="px-4 py-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sectors</span>
          </div>
          {sectors.map((sector) => (
            <Link
              key={sector.id}
              to={`/sector/${sector.id}`}
              className="block px-8 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              {sector.name}
            </Link>
          ))}

          <Link
            to="/news"
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            News
          </Link>

          <Link
            to="/about"
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            About
          </Link>

          <Link
            to="/contact"
            className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>

          <Link
            to="/admin"
            className="block px-4 py-3 text-sm font-medium text-gray-500 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            Admin Panel
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
